import React, {useState, useEffect, useMemo} from 'react';
import { KeyboardAvoidingView, Modal, Platform, ScrollView, TextInput, TouchableOpacity, View } from 'react-native'

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import IcomoonIcon from '../../../../components/IcomoonIcon';
import { Icon } from '../../../../components/Icon';
import { Illustration, IllustrationName } from '../../../../components/Illustration';
import Input from '../../../../components/Forms/Input';
import { useController, useForm, useFormState, useWatch } from 'react-hook-form';
import IconButton from '../../../../components/IconButton';
import useFormSubmit from '../../../../hooks/useFormSubmit';
import DateSelector from '../../../../components/Forms/DateSelector';
import BottomSheet from '../../../../components/BottomSheet';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Image } from 'expo-image';
import dayjs from 'dayjs';
import PassDatesStep from './NewGuessPass/PassDatesStep';
import PassTimeStep from './NewGuessPass/PassTimeStep';
import ProviderTypeStep from './NewGuessPass/ProviderTypeStep';
import DeliveryTypeStep from './NewGuessPass/DeliveryTypeStep';
import EventGuestsStep from './NewGuessPass/EventGuestsStep';
import IntroSection from './IntroSection';
import { useDispatch, useSelector } from 'react-redux';
import { setQrPassIntroShown } from '../../../../store/Reducers/settings';
import { ColorSelector } from '../../Admin/Units/NewVehicleModal';


export function TabsSelector({onChange, values, initialValue}){
  const [selected, setSelected] = useState(initialValue ?? values[0]?.key);

  function renderTab(label, value){
    return (
      <TouchableOpacity
        key={value}
        style={tw.style(`flex-1 items-center py-4 px-2 rounded-2xl  font-500`, selected === value && `bg-primary-800`)}
        onPress={() => {
          setSelected(value)
          onChange?.(value)
        }}
      >
        <Text style={tw.style(`text-primary-800 font-500`, selected === value && `text-white`)}>{label}</Text>
      </TouchableOpacity>
    )
  }

  return (
    <View style={tw`row bg-white rounded-2xl overflow-hidden px-1 py-1`}>
      {
        values.map(value => renderTab(value.title, value.key))
      }
    </View>
  )
}




function GuestTypeSelector({onChange, value, settings}){
  const [selected, setSelected] = useState(value);

  const types: Array<{ key: string; illustration: IllustrationName; title: string; subtitle: string }> = [
    {
      key: 'personal',
      illustration: 'gpPersonal',
      title: 'Personal',
      subtitle: 'Amigos y familiares',
    },
    {
      key: 'provider',
      illustration: 'gpProveedor',
      title: 'Proveedor',
      subtitle: 'Servicios del hogar',
    },
    {
      key: 'meeting',
      illustration: 'gpReunion',
      title: 'Reunión',
      subtitle: 'Varios invitados',
    },
    {
      key: 'delivery',
      illustration: 'gpPaqueteria',
      title: 'Entrega',
      subtitle: 'Paquetería y repartidores',
    },
  ]

  const allowedTypes = useMemo(() => {
    const allowed = settings?.allowed_types?.length ? settings.allowed_types : ['personal', 'meeting']

    return types.filter(t => allowed.includes(t.key))
  }, [settings])


  function onSelect(type){
    setSelected(type)
    onChange(type)
  }

  return (
    <View style={tw`gap-y-5`}>
      {
        allowedTypes.map(type =>
          <GuestTypeButton
            key={type.key}
            illustration={type.illustration}
            title={type.title}
            subtitle={type.subtitle}
            onPress={() => onSelect(type.key)}
            selected={selected == type.key}
          />
        )
      }
    </View>
  )
}

function GuestTypeButton({ illustration, title, subtitle, onPress, selected }: {
  illustration: IllustrationName;
  title: string;
  subtitle: string;
  onPress: () => void;
  selected: boolean;
}){
  return (
    <TouchableOpacity
      style={tw.style(`row bg-white rounded-2xl px-4 py-4`, selected && `bg-primary-800`)}
      onPress={onPress}
    >
      <Illustration
        name={illustration}
        width={32}
        height={36}
        style={tw`mr-5`}
      />

      <View style={tw``}>
        <Text style={tw.style(`font-600 text-secondary text-lg -mb-1`, selected && `text-white`)}>{title}</Text>
        <Text style={tw.style(`text-gray-500 text-sm`, selected && `text-white`)}>{subtitle}</Text>
      </View>
    </TouchableOpacity>
  )
}


export function FooterButtons({
  showCancelButton=false,
  nextDisabled=false,
  onCancelPressed=()=>{},
  onBackPressed=()=>{},
  onNextPressed=()=>{},
  lastStep=false,
  isSubmitting=false,
}){

  return (
    <View style={tw`row justify-between mb-2 mt-2`}>
      {
        showCancelButton ?
          <IconButton title={'Cancelar'} style={tw`py-3 px-10`} color='secondary' onPress={onCancelPressed} />
        :
          <IconButton title={'Atrás'} style={tw`py-3 px-10`} color='primary-800' onPress={onBackPressed} />
      }

      <IconButton
        iconPosition='right'
        icon={lastStep ? null : 'add1'}
        title={lastStep ? (!isSubmitting ? 'Terminar' : 'Enviando') : 'Siguiente'}
        style={tw`py-3 px-10`}
        color={nextDisabled ? 'gray-300': (!lastStep ? 'primary-800' : 'secondary')}
        disabled={nextDisabled || isSubmitting}
        onPress={onNextPressed}
      />
    </View>
  )
}

function GuestTypeStep({ control, name, onNextPressed, onCancelPressed, settings }){
  const { field, fieldState: { invalid, isTouched, isDirty, error }, formState } = useController({
    control,
    name,
    rules: {required: true}
  })

  return (
    <>
      <View style={tw`flex-1`}>
        <Text style={tw`text-white text-center text-xl font-500 mb-8`}>Tipo de invitado</Text>

        <GuestTypeSelector
          onChange={type => {
            field.onChange(type)
          }}
          value={field.value}
          settings={settings}
        />
      </View>

      <FooterButtons
        showCancelButton
        onCancelPressed={onCancelPressed}
        onNextPressed={onNextPressed}
        nextDisabled={!field.value}
      />
    </>
  )
}

function GuestNameStep({ 
  control, setValue, name, onNextPressed, onBackPressed, settings 
}: {
  settings?: Partial<GuestPassSettings>;
}){
  const { field, fieldState: { invalid, isTouched, isDirty, error }, formState } = useController({
    control,
    name,
    rules: {required: true}
  })

  const { guest_vehicle, guest_name } = useWatch({control})

  const vehicleBrandIsValid = useMemo(() => {
    const value = guest_vehicle?.brand_model?.trim() || "";

    // 1. Debe tener al menos 2 palabras (marca + modelo)
    const wordCount = value.split(/\s+/).length;

    // 2. Cada palabra debe tener al menos 2 letras (evita cosas como "A B")
    const hasMinimumWordLength = value.split(/\s+/).every(w => w.length >= 2);

    // 3. Solo letras, números, espacios y guiones (evita símbolos raros)
    const validChars = /^[a-zA-Z0-9\s\-]+$/.test(value);

    // 4. Longitud total razonable (al menos 4 caracteres)
    return (
      value.length >= 4 &&
      wordCount >= 2 &&
      hasMinimumWordLength &&
      validChars
    );
  }, [guest_vehicle?.brand_model])

  const vehiclePlateNumberIsValid = useMemo(() => {
    return (guest_vehicle?.plate_number?.length > 5)
  }, [guest_vehicle?.plate_number])

  const requiresCarDetails = settings?.guest_vehicle_required ?? true;

  const isValid = !!guest_name && (
      !requiresCarDetails || (
        requiresCarDetails && vehicleBrandIsValid && vehiclePlateNumberIsValid
      )
    )

  // Advertencias por campo: se muestran una vez que el usuario tocó el campo.
  const [touched, setTouched] = useState({ name: false, brand: false, plate: false })
  const touch = (key: 'name' | 'brand' | 'plate') => setTouched(t => ({ ...t, [key]: true }))


  return (
    <>
      <ScrollView style={tw`flex-1`} contentContainerStyle={tw`pb-4`} keyboardShouldPersistTaps='handled'>
        <View style={tw``}>
          <Text style={tw`text-white text-center text-xl font-500 mb-6`}>Nombre de tu invitado</Text>

          <Input
            control={control}
            name='guest_name'
            rules={{ required: true }}
            style={{ subcontainer: tw.style(`bg-white`, touched.name && !guest_name && `border border-red`) }}
            placeholder='Nombre completo'
            onBlur={() => touch('name')}
          />

          {
            touched.name && !guest_name &&
              <View style={tw`self-center bg-white rounded-full px-3 py-0.5 -mt-3 mb-2`}>
                <Text style={tw`text-red text-sm`}>Este campo es obligatorio</Text>
              </View>
          }
        </View>

        {
          requiresCarDetails &&
            <View style={tw``}>
              <Text style={tw`text-white text-center text-xl font-500 mb-6 mt-4`}>Detalles del auto</Text>

              <Input
                control={control}
                name='guest_vehicle.brand_model'
                rules={{ required: true }}
                style={{ subcontainer: tw.style(`bg-white`, touched.brand && !vehicleBrandIsValid && `border border-red`) }}
                placeholder='Marca y modelo'
                onBlur={() => touch('brand')}
              />

              {
                touched.brand && !vehicleBrandIsValid &&
                  <View style={tw`self-center bg-white rounded-full px-3 py-0.5 -mt-3 mb-2`}>
                    <Text style={tw`text-red text-sm`}>Escribe marca y modelo</Text>
                  </View>
              }


              <View style={tw`bg-white rounded-xl py-5 px-3 mb-6`}>
                <ColorSelector onChange={(value) => setValue("guest_vehicle.color", value)} />
              </View>


              <TextInput
                // control={control}
                // name='guest_vehicle.plate_number'
                // rules={{ required: true }}
                onChangeText={text => setValue('guest_vehicle.plate_number', text?.toUpperCase?.())}
                onBlur={() => touch('plate')}
                value={guest_vehicle?.plate_number}
                placeholderTextColor={tw.color(`gray-400`)}
                maxLength={9}
                style={tw.style(`bg-white w-50% self-center text-base text-center py-2 rounded-xl border`, (touched.plate && !vehiclePlateNumberIsValid) ? `border-red` : `border-gray-300`)}
                placeholder='Placas'
              />

              {
                touched.plate && !vehiclePlateNumberIsValid &&
                  <View style={tw`self-center bg-white rounded-full px-3 py-0.5 mt-2`}>
                    <Text style={tw`text-red text-sm`}>Este campo es obligatorio</Text>
                  </View>
              }
            </View>
        }
      </ScrollView>

      <FooterButtons
        onBackPressed={onBackPressed}
        onNextPressed={onNextPressed}
        nextDisabled={!isValid}
      />
    </>
  )
}


function IntroScreen({}){
  const [step, setStep] = useState(1);
  const dispatch = useDispatch()
  
  return (
    <>
      {
        step == 1 &&
          <IntroSection onNextPressed={() => dispatch(setQrPassIntroShown(true)) } />
      }
    </>
  )
}


export type GuestType = 'personal' | 'provider' | 'meeting' | 'delivery';

export interface GuestPassSettings {
  allowed_types: GuestType[];
  resident_can_create: boolean;
  guest_vehicle_required?: boolean;
}

export const ALL_GUEST_TYPES: ReadonlyArray<GuestType> = [
  'personal',
  'provider',
  'meeting',
  'delivery',
] as const;

export function canCreateGuestPass(settings?: Partial<GuestPassSettings>): boolean {
  return settings?.resident_can_create ?? true;
}



export default function NewGuestPassModal({ 
  onRequestClose, visible, communityId, unitId, onGuestPassCreated, settings 
}: {
  onRequestClose: () => void;
  visible: boolean;
  communityId: string;
  unitId: string;
  onGuestPassCreated: () => void;
  settings?: Partial<GuestPassSettings>;
}) {
  const { control, watch, setValue, handleSubmit, reset, trigger } = useForm({
    defaultValues: {
      guest_type: null,
      guest_name: null,
      periodicity: "one_time",
      entry_date: new Date(),
      // entry_date_limit: null,
      entry_time: 'all_day',
      guest_vehicle: {}
      // entry_time_limit: null,
    },
    mode: 'onChange',
  });

  const { qrPassIntroShown } = useSelector(state => state.settings)

  const [step, setStep] = useState(1);
  const insets = useSafeAreaInsets()


  const guest_type = watch('guest_type')
  

  const { submitForm, isSubmitting } = useFormSubmit({
    onSuccess: onGuestPassCreated,
    onError: (error) => { },
    successToastMessage: "Pase creado",
    endpoint: `/communities/${communityId}/units/${unitId}/entry-passes`,
  })

  useEffect(() => {
    setTimeout(() => {

      trigger()
    }, 1000);

  }, []);


  useEffect(() => {
    reset()
    setStep(1)
  }, [visible]);


  function submitPass(data){
    // console.log('submitpass', data)
    submitForm(data)
  }


  return (
    <Modal
      onRequestClose={onRequestClose}
      visible={visible}
      statusBarTranslucent={true}
      animationType='slide'
    >
      <View style={tw`px-5 flex-1 bg-primary pt-[${insets.top}px]`}>
        {
          qrPassIntroShown &&
            <>
              {/* Header */}
              <View style={tw`mb-4`}>
                <View style={tw`row justify-between mb-4 mt-5`}>
                  <View style={tw`row`}>
                    <Icon name="qr" size={40} style={tw`mr-3`} />

                    <Text style={tw`text-lg font-600 text-secondary`}>Nuevo QR de invitado</Text>
                  </View>

                  <TouchableOpacity
                    style={tw`items-center justify-center mr-2`}
                    onPress={onRequestClose}
                  >
                    <IcomoonIcon name='close' style={tw`text-red`} size={26} />
                  </TouchableOpacity>
                </View>
              </View>
            </>
        }

        {
          ! qrPassIntroShown &&
            <IntroScreen />
        }
        

        {/* Bloque que abarca todo el espacio */}
        {
          qrPassIntroShown &&
          <KeyboardAvoidingView
            style={tw`flex-1`}
            behavior={(Platform.OS === 'ios') ? "padding" : "padding"}
            contentContainerStyle={tw`flex-1`}
          >
            {
              step == 1 &&
                <GuestTypeStep
                  control={control}
                  settings={settings}
                  name={'guest_type'}
                  onCancelPressed={() => onRequestClose()}
                  onNextPressed={() => setStep(step+1)}
                />
            }

            {
              (step == 2 && guest_type == 'personal') &&
                <GuestNameStep
                  control={control}
                  settings={settings}
                  setValue={setValue}//! dafuq
                  name={'guest_name'}
                  onBackPressed={() => {
                    setStep(step - 1)
                  }}
                  onNextPressed={() => setStep(step+1)}
                />
            }

            {
              (step == 2 && guest_type == 'provider') &&
                <ProviderTypeStep
                  control={control}
                  name={'provider_type'}
                  setValue={setValue}//!! EEEJJJ :S
                  onBackPressed={() => {
                    setStep(step - 1)
                  }}
                  onNextPressed={() => setStep(step+1)}
                />
            }

            {
              (step == 2 && guest_type == 'delivery') &&
                <DeliveryTypeStep
                  control={control}
                  name={'provider_type'}
                  setValue={setValue}//!! EEEJJJ :S
                  onBackPressed={() => {
                    setStep(step - 1)
                  }}
                  onNextPressed={() => setStep(step+1)}
                />
            }
            {
              (step == 2 && guest_type == 'meeting') &&
                <EventGuestsStep
                  control={control}
                  name={'guest_name'}
                  setValue={setValue}//!! EEEJJJ :S
                  onBackPressed={() => {
                    setStep(step - 1)
                  }}
                  onNextPressed={() => setStep(step+1)}
                />
            }


            {
              (step == 3) &&
                <PassTimeStep
                  control={control}
                  setValue={setValue}//!! EEEJJJ :S
                  // name={'guest_name'}
                  onBackPressed={() => {
                    setStep(step - 1)
                  }}
                  isLastStep={guest_type == 'meeting'}
                  onNextPressed={() => {
                    if (guest_type == 'meeting'){
                      handleSubmit(submitPass)()
                    }else{
                      setStep(step+1)
                    }
                  }}
                  isSubmitting={isSubmitting}
                />
            }

            {
              (step == 4) &&
                <PassDatesStep
                  control={control}
                  setValue={setValue}//!! EEEJJJ :S
                  onBackPressed={() => { setStep(step - 1) }}
                  onNextPressed={handleSubmit(submitPass)}
                  isSubmitting={isSubmitting}
                />
            }
          </KeyboardAvoidingView>
        }

        {/* <IconButton
          disabled={isSubmitting}
          title={"Crear Pase"}
          style={tw`rounded-b-0 -mx-5 rounded-2xl py-3`}
          onPress={handleSubmit(submitForm)}
        /> */}

      </View>
    </Modal>
  );
}

